import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileSignature } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { Button } from './Button';
import { SignatureCanvas } from './SignatureCanvas';

interface AddendumStatus {
  nodig: boolean;
  status: 'open' | 'getekend' | null;
  addendum: null | {
    id: string;
    versie: string;
    contract_version: string;
    contract_getekend_op: string | null;
    tekst: string;
    sha256: string;
    getekend_op: string | null;
  };
}

export const ADDENDUM_KEY = ['contract-addendum'];

/**
 * Pop-up voor het entiteitsaddendum bij de v2.0-Gebruikersovereenkomst
 * (besluit 14, migratie 200). Staat op het dashboard en op de urenpagina
 * ("bij de eerstvolgende urensluiting"). Geen harde blokkade: "Later" sluit
 * de pop-up voor dit bezoek; bij het volgende bezoek komt hij terug zolang
 * het addendum ongetekend is.
 */
export const ContractAddendumPopup: React.FC<{ context?: 'dashboard' | 'uren' }> = ({ context = 'dashboard' }) => {
  const { t } = useTranslation('professional');
  const queryClient = useQueryClient();
  const [gesloten, setGesloten] = useState(false);
  const [handtekening, setHandtekening] = useState<string | null>(null);
  const [akkoord, setAkkoord] = useState(false);

  const { data } = useQuery<AddendumStatus>({
    queryKey: ADDENDUM_KEY,
    queryFn: async () => (await api.get('/professional/contract-addendum')).data.data,
    staleTime: 60_000,
  });

  const teken = useMutation({
    mutationFn: () =>
      api.post('/professional/contract-addendum/teken', {
        handtekening,
        akkoord,
        sha256: data?.addendum?.sha256,
      }),
    onSuccess: () => {
      toast.success(t('addendum.getekend', 'Addendum getekend. Dank u wel.'));
      queryClient.invalidateQueries({ queryKey: ADDENDUM_KEY });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('addendum.fout', 'Het addendum kon niet worden getekend.'));
      queryClient.invalidateQueries({ queryKey: ADDENDUM_KEY });
    },
  });

  if (gesloten || !data?.nodig || data.status !== 'open' || !data.addendum) return null;

  const intro = context === 'uren'
    ? t('addendum.introUren', 'Voordat u uw uren van deze maand afsluit: teken het addendum bij uw overeenkomst. Uw uren blijven gewoon beschikbaar.')
    : t('addendum.introDashboard', 'Wij vragen u een addendum bij uw overeenkomst te tekenen. Het legt vast met welke rechtspersoon u de overeenkomst heeft gesloten.');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="addendum-titel">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl">
        <div className="flex items-start gap-3 border-b border-gray-200 p-5">
          <FileSignature className="mt-0.5 h-6 w-6 shrink-0 text-navy-700" />
          <div>
            <h2 id="addendum-titel" className="text-lg font-semibold text-gray-900">
              {t('addendum.titel', 'Addendum bij uw Gebruikersovereenkomst')}
            </h2>
            <p className="mt-1 text-sm text-gray-600">{intro}</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <div className="whitespace-pre-line rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm leading-relaxed text-gray-800">
            {data.addendum.tekst}
          </div>

          <p className="mt-5 mb-2 text-sm font-medium text-gray-900">{t('addendum.handtekening', 'Uw handtekening')}</p>
          <SignatureCanvas
            width={500}
            height={160}
            onSave={setHandtekening}
            onClear={() => setHandtekening(null)}
          />

          <label className="mt-4 flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" className="mt-1" checked={akkoord} onChange={(e) => setAkkoord(e.target.checked)} />
            <span>{t('addendum.akkoord', 'Ik ga akkoord met dit addendum en teken het digitaal.')}</span>
          </label>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 p-4">
          <Button type="button" variant="outline" onClick={() => setGesloten(true)}>
            {t('addendum.later', 'Later')}
          </Button>
          <Button type="button" onClick={() => teken.mutate()} disabled={!handtekening || !akkoord || teken.isPending}>
            {teken.isPending ? t('addendum.bezig', 'Bezig…') : t('addendum.teken', 'Addendum tekenen')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContractAddendumPopup;
